interface AccordionElement extends HTMLElement {
    accordion?: Accordion;
}

export class Accordion {
    private isAnimating = false;
    private animationTimeout?: number;
    private readonly ANIMATION_DURATION = 500;

    constructor(
        private element: HTMLElement,
        private parent: HTMLElement
    ) {
        this.init();
    }

    private init(): void {
        this.parent.querySelectorAll('a[role="tab"][href^="#"], div[role="tab"] svg, .accordion-toggle')
            .forEach(button => button.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggle();
            }));
    }

    public toggle(): void {
        this.element.classList.contains('-active') ? this.collapse() : this.open();
    }

    public open(): void {
        if (this.isAnimating) return;
        
        this.isAnimating = true;
        clearTimeout(this.animationTimeout);
        
        this.element.style.height = 'auto';
        this.element.style.visibility = 'hidden';
        this.element.classList.add('-active');
        const height = this.element.offsetHeight;
        
        this.element.style.height = '0';
        this.element.style.visibility = '';
        this.element.classList.add('-animating');
        this.parent.classList.add('-active');
        
        requestAnimationFrame(() => {
            this.element.style.height = `${height}px`;
            this.animationTimeout = window.setTimeout(() => {
                this.element.style.removeProperty('height');
                this.element.classList.remove('-animating');
                this.isAnimating = false;
            }, this.ANIMATION_DURATION);
        });
    }

    public collapse(): void {
        if (this.isAnimating) return;
        
        this.isAnimating = true;
        clearTimeout(this.animationTimeout);
        
        const height = this.element.offsetHeight;
        this.element.style.height = `${height}px`;
        this.element.classList.add('-animating');
        this.parent.classList.remove('-active');
        
        requestAnimationFrame(() => {
            this.element.style.height = '0';
            this.animationTimeout = window.setTimeout(() => {
                this.element.style.removeProperty('height');
                this.element.classList.remove('-active', '-animating');
                this.isAnimating = false;
            }, this.ANIMATION_DURATION);
        });
    }
}

export class Accordions {
    private observer: MutationObserver;

    constructor() {
        this.initializeAccordions();
        this.setupObserver();
    }

    private initializeAccordions(): void {
        document.querySelectorAll<HTMLElement>('.accordion:not(.accordion--disabled) > div .accordion-content')
            .forEach(this.createAccordion);
    }

    private createAccordion = (content: HTMLElement): void => {
        if ((content as AccordionElement).accordion) {
            return;
        }

        const parent = content.parentElement;
        if (parent) {
            (content as AccordionElement).accordion = new Accordion(content, parent);
        }
    }

    private setupObserver(): void {
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node instanceof HTMLElement) {
                            if (node.matches('.accordion:not(.accordion--disabled) > div .accordion-content')) {
                                this.createAccordion(node);
                            }
                            node.querySelectorAll<HTMLElement>('.accordion:not(.accordion--disabled) > div .accordion-content')
                                .forEach(this.createAccordion);
                        }
                    });
                }
            });
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    public destroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

export default Accordions;